import { guestLinks } from '@map/components/GuestArea/links';
import {
  FormOneTimeCodeField,
  FormPasswordField,
  FormTextField,
} from '@map/components/formControls/controlled';
import { useLogin } from '@map/hooks/auth';
import { useUpdateAuthenticationFlow } from '@map/hooks/flows/authentication';
import useFeatureIsAccessible from '@map/hooks/useFeatureIsAccessible';
import { makeStyles } from '@map/theme';
import { oneTimeCodeValidator, phoneChecker, sanitizeUsername } from '@map/validators/user';
import { Typography } from '@mui/material';
import useAvailableProviders from '@shared/hooks/auth/useAvailableProviders';
import { Form } from '@shared/lib/form';
import { SubmitButton, useFormField } from '@shared/lib/form/Form';
import Yup from '@shared/lib/yupWithCustomValidations';
import { getConfigString } from '@shared/util/env';
import Button from '@ui/Button';
import FormDivider from '@ui/FormDivider';
import NafathIcon from '@ui/Icon/library/NafathIcon';
import NavButton from '@ui/NavButton';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import SocialAuthButton from '../shared/SocialAuthButton';

const useStyles = makeStyles(({ spacing }) => ({
  socialButtons: {
    display: 'grid',
    gap: spacing(11),
  },
  socialButtonsCompact: {
    gridAutoFlow: 'column',
    gridAutoColumns: 'min-content',
    justifyContent: 'center',
    alignItems: 'center',
  },
  forgotPassword: {
    margin: spacing(2.5, 0, 4),
  },
  otpField: {
    margin: spacing(3, 0, 6),
  },
}));

const initialValues = { username: '', password: '', otp_attempt: '' };

const validationSchema = Yup.object({
  username: Yup.string().notBlank().validUsername(),
  password: Yup.string().notBlank(),
});

const otpValidationSchema = Yup.object({ otp_attempt: oneTimeCodeValidator });

const ForgotPasswordButton = () => {
  const { classes } = useStyles();
  const { value } = useFormField<string>('username');
  return (
    <NavButton
      variant="tertiary"
      className={classes.forgotPassword}
      to={{
        pathname: guestLinks.forgotPassword(),
        state: { username: value },
      }}
    >
      <FormattedMessage id="login.forgot_password_link" />
    </NavButton>
  );
};

const LoginForm = () => {
  const { classes, cx } = useStyles();
  const history = useHistory<any>();
  const phoneNumberAuth = useFeatureIsAccessible('phoneNumberAuth');
  const emailAuth = useFeatureIsAccessible('emailAuth');
  const isNefathAuthAccessible = useFeatureIsAccessible('nefathSignin');
  const standardLoginAvailable = emailAuth || phoneNumberAuth;
  const login = useLogin();
  const updateAuthenticationFlow = useUpdateAuthenticationFlow();
  const [showOtp, setShowOtp] = useState(false);
  const availableProviders = useAvailableProviders();

  const handleSubmit = useCallback(
    async (values: typeof initialValues) => {
      const { from } = history.location.state || { from: { pathname: '/' } };
      const { username: unsanitaryUsername, password, ...rest } = values;
      const username = sanitizeUsername(unsanitaryUsername);
      const isPhoneNumber = phoneChecker.isValidSync(username);
      const { success, requestOtp } = await login(
        showOtp ? { username, password, ...rest } : { username, password },
      );

      if (success) {
        updateAuthenticationFlow({ action: 'sign_in', method: isPhoneNumber ? 'phone' : 'email' });
        history.replace(from);
      }
      if (requestOtp) {
        setShowOtp(true);
      }
    },
    [login, showOtp, updateAuthenticationFlow, history],
  );

  if (showOtp) {
    return (
      <Form
        initialValues={initialValues}
        validationSchema={otpValidationSchema}
        onSubmit={handleSubmit}
        validationMode="onBlur"
      >
        <Typography variant="subtitle1" align="center" gutterBottom>
          <FormattedMessage id="login.otp_label" />
        </Typography>
        <Typography variant="body2" align="center">
          <FormattedMessage id="login.otp_description" />
        </Typography>

        <div className={classes.otpField}>
          <FormOneTimeCodeField name="otp_attempt" autoFocus />
        </div>

        <SubmitButton fullWidth={true} size="medium">
          <FormattedMessage id="login.submit_button" />
        </SubmitButton>
      </Form>
    );
  }

  return (
    <>
      {isNefathAuthAccessible && (
        <>
          <form action="/users/auth/nefath" method="POST">
            <Button type="submit" startIcon={<NafathIcon />} fullWidth={true} size="medium">
              <FormattedMessage id="login.login_with_nefath" />
            </Button>
            {/*eslint-disable-next-line react/forbid-elements*/}
            <input hidden name="authenticity_token" value={getConfigString('csrf-token')} />
          </form>
          {standardLoginAvailable && <FormDivider />}
        </>
      )}
      {standardLoginAvailable && (
        <Form
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validationMode="onBlur"
        >
          <FormTextField
            name="username"
            placeholderIntlKey="login.username_label"
            autoComplete="username"
            margin="dense"
          />
          <FormPasswordField
            name="password"
            placeholderIntlKey="login.password_label"
            autoComplete="current-password"
            margin="dense"
          />
          <ForgotPasswordButton />

          <SubmitButton fullWidth={true} size="medium">
            <FormattedMessage id="login.submit_button" />
          </SubmitButton>
        </Form>
      )}

      {standardLoginAvailable && availableProviders.length > 0 && <FormDivider />}

      {availableProviders.length > 0 && (
        <div
          className={cx(
            classes.socialButtons,
            standardLoginAvailable && classes.socialButtonsCompact,
          )}
        >
          {availableProviders.map((provider) => (
            <SocialAuthButton
              key={provider}
              provider={provider}
              label="signin_with"
              icon={standardLoginAvailable}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default LoginForm;
